import { apiFetch, TENANT } from "../api.js";
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

/** `scenarioId` is null for a tenant with no published scenario — see readPlan. */
export interface AnswerScenario { scenarioId: string | null; name: string; published: boolean }
export interface AnswerColumn { key: string; label: string }

/** Which plan the answer read, said the way a person would say it. */
export function planLabel(s: AnswerScenario): string {
  return s.published ? "the live schedule" : `draft “${s.name}”`;
}

/**
 * One row, in column order, as strings.
 *
 * Null is an em dash and never the word "null"; zero is zero and is never
 * mistaken for missing, which a truthiness check would do.
 */
export function answerCells(
  row: Record<string, string | number | null>,
  columns: AnswerColumn[],
): string[] {
  return columns.map((c) => {
    const v = row[c.key];
    return v === null || v === undefined ? "—" : String(v);
  });
}

export function citationLabel(c: { id: string; title: string }): string {
  return c.title.trim() || c.id;
}

export type RevealTarget = { kind: "phase" | "resource"; id: string };

/**
 * What a row points at.
 *
 * Phase wins over person: an assignments row carries both, and the phase is the
 * thing the reader just asked about. A row with neither — an absence range, say
 * — is not a link, and rendering it as one would teach people that half the
 * table does nothing when clicked.
 */
export function rowTarget(row: Record<string, unknown>): RevealTarget | null {
  if (typeof row.phase === "string" && row.phase) return { kind: "phase", id: row.phase };
  if (typeof row.resource === "string" && row.resource) return { kind: "resource", id: row.resource };
  return null;
}

/**
 * The "could not express" turn, in words that fit the path that produced it.
 *
 * `unmapped` is shared by all four destinations, and the block's copy was the
 * planner's: "No term covers this, so it was left out rather than
 * approximated." Terms are what a plan compiles to. A question compiles to a
 * query and a help answer compiles to nothing at all, so for three of the four
 * paths that sentence explained a mechanism that was not involved.
 *
 * Null suppresses the turn. An ungrounded help answer has already said it has
 * no topic for the question, in its own words, in the turn above — and saying
 * it a second time with a different explanation is how a refusal starts
 * looking like two separate failures.
 */
export function unmappedBlock(
  r: { stage?: string; unmapped?: string[]; grounded?: boolean },
): { title: string; items: string[]; note: string } | null {
  const items = r.unmapped ?? [];
  if (!items.length) return null;
  if (r.stage === "help" && r.grounded === false) return null;

  if (r.stage === "help") {
    return { title: "Not in the help", items, note: "The help written for this tool does not cover this part." };
  }
  if (r.stage === "answer") {
    return {
      title: "Could not answer",
      items,
      note: "There is nothing in the schedule I could read this from, so I left it out rather than guessing.",
    };
  }
  return {
    title: "Could not express",
    items,
    note: "No term covers this, so it was left out rather than approximated.",
  };
}

/** The compiled question, as the server returns it. The read path's `spec`. */
export interface AnswerQuery {
  ask: string;
  select: Record<string, unknown>;
  window?: { from?: string; to?: string };
  hours?: number;
  limit?: number;
}

/**
 * The parts of a compiled question that are not the subject and the selector.
 *
 * Said in words rather than shown as JSON, because this is what "Interpret
 * only" is for: the reader is checking whether the machine understood them,
 * and `{"to":"2026-10-09"}` does not answer that question as well as "through
 * 2026-10-09" does. Absent fields produce no line — a question with no window
 * has no window to disagree with.
 */
export function queryQualifiers(q: AnswerQuery): string[] {
  const out: string[] = [];
  if (q.window?.from || q.window?.to) {
    out.push(`from ${q.window.from ?? "today"} through ${q.window.to ?? "the end of the horizon"}`);
  }
  if (typeof q.hours === "number") out.push(`${q.hours} hours to place`);
  if (typeof q.limit === "number") out.push(`the first ${q.limit}`);
  return out;
}

export const ASK_WIDTH_DEFAULT = 440;
const ASK_WIDTH_MIN = 360;
const ASK_WIDTH_KEY = "sentry.ask.width";

/**
 * How wide the panel may be, given the window it sits in.
 *
 * A wide answer table is the reason to drag, so the ceiling is the window, not
 * a fixed number — less a strip of board, so the thing you asked about stays
 * visible behind it. The floor wins on a window too narrow for both.
 */
export function clampAskWidth(w: number, viewport: number): number {
  const max = Math.max(ASK_WIDTH_MIN, viewport - 120);
  if (!Number.isFinite(w)) return ASK_WIDTH_DEFAULT;
  return Math.round(Math.min(max, Math.max(ASK_WIDTH_MIN, w)));
}

function savedAskWidth(): number {
  try {
    const n = Number(localStorage.getItem(ASK_WIDTH_KEY));
    return n ? clampAskWidth(n, window.innerWidth) : ASK_WIDTH_DEFAULT;
  } catch {
    return ASK_WIDTH_DEFAULT;
  }
}

interface AskResult {
  stage?: string;
  spec?: { hard: any[]; soft: any[]; horizon: { from: string; to: string } };
  query?: AnswerQuery | null;
  unmapped?: string[];
  absences?: { summary: string }[];
  issues?: { message: string; suggestions?: string[] }[];
  outcome?: { status: string; wallTimeS: number; taskCount: number; churn?: number; infeasibleCore?: string[] };
  explanation?: string;
  scenarioId?: string | null;
  error?: string;
  emptySelectors?: { termId: string; term: string; select: Record<string, unknown> }[];
  hint?: string;
  kind?: "query" | "why";
  scenario?: AnswerScenario;
  columns?: AnswerColumn[];
  rows?: Record<string, string | number | null>[];
  lead?: string;
  citations?: { id: string; title: string }[];
  text?: string;
  grounded?: boolean;
}

/**
 * Ask is not a destination. ⌘K opens it over whatever board you are on, and the
 * panel docks beside it rather than replacing it.
 */
export function Ask({
  open,
  onClose,
  onPublished,
  context,
  scenarioId,
  onReveal,
}: {
  open: boolean;
  onClose: () => void;
  onPublished: (scenarioId: string) => void;
  context: string;
  /** Ask refines the scenario you are looking at rather than minting another. */
  scenarioId?: string;
  /**
   * Opens the phase or person a row names, on the board behind the panel.
   * Nobody passes this yet: the board has no focus, highlight or scroll-to
   * state for a handler to set. `BoardFilter` (Filters.tsx) narrows which
   * rows the Gantts render rather than focusing one, and carries no
   * `phaseId` at all. Wiring this is a decision about the board, not a
   * change here — until it exists, `rowTarget` and the row's `clickable`
   * class are inert.
   */
  onReveal?: (target: RevealTarget) => void;
}) {
  const [intent, setIntent] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AskResult | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const [width, setWidth] = useState(savedAskWidth);

  // Escape closes the panel, so it can always be dismissed from the keyboard.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) setTimeout(() => input.current?.focus(), 30);
  }, [open]);

  // A window made smaller must not leave the panel wider than it.
  useEffect(() => {
    const fit = () => setWidth((w) => clampAskWidth(w, window.innerWidth));
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  function saveWidth(w: number) {
    try { localStorage.setItem(ASK_WIDTH_KEY, String(w)); } catch { /* private window: the width just isn't remembered */ }
  }

  /** The panel is docked right, so dragging its left edge leftward widens it. */
  function startResize(e: ReactPointerEvent<HTMLDivElement>) {
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const startW = width;
    let last = startW;
    const move = (ev: PointerEvent) => {
      last = clampAskWidth(startW + (startX - ev.clientX), window.innerWidth);
      setWidth(last);
    };
    const up = () => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", up);
      handle.removeEventListener("pointercancel", up);
      document.body.style.userSelect = "";
      saveWidth(last);
    };
    document.body.style.userSelect = "none";
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", up);
    handle.addEventListener("pointercancel", up);
  }

  async function run(dryRun: boolean) {
    if (!intent.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await apiFetch("/v1/sentry/ask", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenantId: TENANT, intent, dryRun, scenarioId }),
      });
      const j = await res.json().catch(() => ({} as AskResult));

      // A refusal is information — the server's sentence names the rule that
      // fired, and it is shown verbatim rather than translated into a code.
      if (!res.ok && (res.status === 403 || res.status === 409)) {
        setResult({ error: j.error ?? "That was refused." });
        return;
      }
      setResult(j);

      // Only a plan changes anything, so only a plan refreshes the board.
      if (j.stage === "done" && j.scenarioId) {
        setIntent("");
        onPublished(j.scenarioId);
      }
    } catch (e) {
      setResult({ error: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;
  const o = result?.outcome;
  const unexpressed = result ? unmappedBlock(result) : null;

  return (
    <aside className="ask" style={{ width }}>
      {/* Drag to widen when an answer table is wider than the panel; double-click
          to go back. Styled here because styles.css is the client's brand. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize Ask panel"
        title="Drag to resize · double-click to reset"
        onPointerDown={startResize}
        onDoubleClick={() => { setWidth(ASK_WIDTH_DEFAULT); saveWidth(ASK_WIDTH_DEFAULT); }}
        style={{ position: "absolute", top: 0, bottom: 0, left: -4, width: 8, cursor: "col-resize", zIndex: 1, touchAction: "none" }}
      />
      <div className="ask-head">
        <span className="ask-title">Ask</span>
        <span className="chip">{context}</span>
        <span className="spacer" />
        <button className="btn" onClick={onClose}>Close</button>
      </div>

      <div className="ask-body">
        {result?.spec && (
          <div className="ask-turn">
            <span className="who">Compiled</span>
            <div>
              {result.spec.hard.map((t) => (
                <div className="term" key={t.id}>
                  <b>hard</b> {t.term} <span className="sel">{JSON.stringify(t.select)}</span>
                  {t.bound ? ` → ${t.bound}` : ""}{typeof t.pct === "number" ? ` → ${t.pct}%` : ""}
                </div>
              ))}
              {result.spec.soft.map((t) => (
                <div className="term" key={t.id}>
                  <b className="soft">soft</b> {t.term} <span className="sel">{JSON.stringify(t.select)}</span> · w {t.weight}
                </div>
              ))}
              <div className="term muted">horizon {result.spec.horizon.from} → {result.spec.horizon.to}</div>
            </div>
          </div>
        )}

        {/* A question's compiled form, in the same place a plan's appears.
            "Interpret only" sends dryRun for every kind of sentence, and the
            read path's dry run stops after this — so without it the panel
            rendered a turn with nothing in it but "read from the live
            schedule". The interpretation is the useful thing to preview, and
            the response was already carrying it. */}
        {result?.query && (
          <div className="ask-turn">
            <span className="who">Compiled</span>
            <div>
              <div className="term">
                <b>{result.query.ask}</b> <span className="sel">{JSON.stringify(result.query.select)}</span>
              </div>
              {queryQualifiers(result.query).map((line) => (
                <div className="term muted" key={line}>{line}</div>
              ))}
            </div>
          </div>
        )}

        {/* An absence changes the shared calendar, not just this scenario, so
            it is stated plainly rather than folded into the explanation. */}
        {!!result?.absences?.length && (
          <div className="ask-turn">
            <span className="who">Time off</span>
            <div>
              {result.absences.map((a) => <div key={a.summary}>{a.summary}</div>)}
              <div className="muted" style={{ marginTop: 4 }}>
                Recorded on the calendar for those days. Everyone's schedule sees it, not just this scenario.
              </div>
            </div>
          </div>
        )}

        {unexpressed && (
          <div className="ask-turn warn">
            <span className="who">{unexpressed.title}</span>
            <div>
              {unexpressed.items.map((u) => <div key={u}>“{u}”</div>)}
              <div className="muted" style={{ marginTop: 4 }}>{unexpressed.note}</div>
            </div>
          </div>
        )}

        {!!result?.issues?.length && (
          <div className="ask-turn warn">
            <span className="who">Rejected</span>
            <div>
              {result.issues.map((i) => (
                <div key={i.message}>
                  {i.message}
                  {i.suggestions?.length ? <span className="muted"> — did you mean {i.suggestions.join(", ")}?</span> : null}
                </div>
              ))}
            </div>
          </div>
        )}

        {!!result?.emptySelectors?.length && (
          <div className="ask-turn warn">
            <span className="who">Matched nothing</span>
            <div>
              {result.emptySelectors.map((e) => (
                <div key={e.termId}>{e.termId} — {JSON.stringify(e.select)}</div>
              ))}
              <div className="muted" style={{ marginTop: 4 }}>{result.hint}</div>
            </div>
          </div>
        )}

        {o && (
          <div className={`ask-turn${o.status === "INFEASIBLE" ? " warn" : ""}`}>
            <span className="who">Solver</span>
            <div>
              <div className="term">
                <b>{o.status}</b> · {o.wallTimeS}s · {o.taskCount} phases
                {typeof o.churn === "number" ? ` · ${o.churn} moved` : ""}
              </div>
              {!!o.infeasibleCore?.length && (
                <div className="term">conflict: {o.infeasibleCore.join(" · ")}</div>
              )}
            </div>
          </div>
        )}

        {/* An answer turn needs something to say. A dry run has neither lead
            nor rows — it stopped at the compile, which is rendered above — and
            an empty bubble reading only "read from the live schedule" is not a
            turn, it is a rendering accident. */}
        {result?.stage === "answer" && !!(result.lead || result.rows?.length) && (
          <div className="ask-turn">
            <span className="who ai">Copilot</span>
            <div>
              <div className="say">{result.lead}</div>
              {!!result.rows?.length && (
                <div className="ask-table-wrap">
                  <table className="ask-table">
                    <thead>
                      <tr>{result.columns!.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
                    </thead>
                    <tbody>
                      {result.rows.map((row, i) => {
                        const target = onReveal ? rowTarget(row) : null;
                        return (
                          <tr
                            key={i}
                            className={target ? "clickable" : undefined}
                            onClick={target ? () => onReveal!(target) : undefined}
                          >
                            {answerCells(row, result.columns!).map((cell, j) => <td key={j}>{cell}</td>)}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {result.scenario && (
                <div className="term muted">read from {planLabel(result.scenario)}</div>
              )}
            </div>
          </div>
        )}

        {result?.stage === "help" && (
          <div className={`ask-turn${result.grounded === false ? " warn" : ""}`}>
            <span className="who ai">Copilot</span>
            <div>
              <div className="say">{result.text}</div>
              {!!result.citations?.length && (
                <div className="term muted">
                  from {result.citations.map(citationLabel).join(" · ")}
                </div>
              )}
            </div>
          </div>
        )}

        {result?.explanation && (
          <div className="ask-turn">
            <span className="who ai">Copilot</span>
            <div className="say">{result.explanation}</div>
          </div>
        )}

        {result?.error && (
          <div className="ask-turn warn"><span className="who">Error</span><div>{result.error}</div></div>
        )}

      </div>

      <div className="ask-input">
        <textarea
          ref={input}
          rows={3}
          value={intent}
          placeholder="Describe the outcome you want — “get 216311 back on track without pushing 221965 or 220081 any later”"
          onChange={(e) => setIntent(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void run(false);
            if (e.key === "Escape") onClose();
          }}
        />
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn" disabled={busy} onClick={() => void run(true)}>
            {busy ? "…" : "Interpret only"}
          </button>
          <button className="btn on" disabled={busy} onClick={() => void run(false)}>
            {busy ? "Working…" : "Ask (⌘⏎)"}
          </button>
        </div>
      </div>
    </aside>
  );
}
